import { Dropdown, DropdownItem, DropdownItemProps, HorizontalButtonGroup, HorizontalButtonGroupButton } from '@frontend/components/common';
import { LuEllipsisVertical } from 'react-icons/lu';

import { useCard } from './card.controller';

export interface CardProps<T> {
    id: string;
    item: T;
    title: (item: T) => React.ReactNode;
    description: string;
    tags?: string[];
    tagDisplayMap?: Map<string, React.ReactNode>;
    label?: React.ReactNode;
    loadingTitle?: React.ReactNode;
    cardClassName?: string;
    cardHeaderClassName?: string;
    cardBodyClassName?: string;
    onClick: () => void;
    optionsButtonProps?: {
        items: DropdownItemProps[];
    };
    overrideOptionsButton?: (item: T) => React.ReactNode;
    actionButtons?: (item: T) => HorizontalButtonGroupButton[];
}

export const Card = <T extends { id: string }>(props: CardProps<T>): React.ReactNode => {
    const viewProps = useCard<T>(props);

    return (
        <div
            onClick={viewProps.onClick}
            className={
                props.cardClassName !== undefined
                    ? props.cardClassName
                    : 'bg-background-surface outline-border-default inline-flex h-64 flex-col items-start justify-between self-stretch overflow-hidden rounded-xl p-4 outline-1 outline-offset-[-1px]'
            }>
            <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                <div
                    className={
                        props.cardHeaderClassName !== undefined ? props.cardHeaderClassName : 'inline-flex items-start justify-start gap-2 self-stretch'
                    }>
                    <div className='text-foreground-default h-12 flex-1 justify-start text-xl font-semibold'>{props.title(props.item)}</div>
                    {props.overrideOptionsButton ? (
                        props.overrideOptionsButton(props.item)
                    ) : (
                        <div className='relative h-6 w-6 overflow-hidden'>
                            <Dropdown
                                label={(<LuEllipsisVertical className='text-brand-muted size-6' />) as any}
                                buttonStyle=' '
                                hideChevron>
                                {props.optionsButtonProps?.items?.map((item, index) => (
                                    <DropdownItem
                                        key={index}
                                        {...item}
                                    />
                                ))}
                            </Dropdown>
                        </div>
                    )}
                </div>
                <div
                    className={
                        props.cardBodyClassName !== undefined
                            ? props.cardBodyClassName
                            : 'text-foreground-default justify-start self-stretch text-base font-medium'
                    }>
                    {props.description}
                </div>
            </div>
            <div className='inline-flex w-full justify-between'>
                <div className='inline-flex w-full items-start justify-start gap-2'>
                    {props.actionButtons ? (
                        <HorizontalButtonGroup
                            direction='left'
                            buttons={props.actionButtons(props.item)}
                        />
                    ) : null}

                    {props.tags?.map((key) => (
                        <div
                            key={key}
                            data-variant='Default'
                            className='bg-border-default flex h-6 items-center justify-start gap-2.5 rounded px-1'>
                            <div className='text-foreground-default justify-start text-sm font-medium'>{props.tagDisplayMap?.get(key) ?? key}</div>
                        </div>
                    ))}
                </div>
                {props.label}
            </div>
        </div>
    );
};
